import type Hls from 'hls.js';
import { ensureHls, canPlayNativeHls, focusConfig } from '@/engine/hls';
import { makeVideo, playSafe } from '@/engine/video';
import { pool } from '@/engine/pool';
import { masterUrl, LEVEL_IDX } from '@/config/streams';
import { isYoonee, cctvStreamUrl, cctvTokenExpiry } from '@/config/cctv';
import { QUALITY_CYCLE } from '@/config';
import { state, save, labelOf, effectiveQuality, viewCams } from '@/store/state';
import { refreshQualityLabels } from './grid';
import { ICON } from '@/lib/icons';
import { must, esc, clamp, dist, sleep } from '@/lib/dom';
import type { Quality } from '@/engine/types';
import * as bus from '@/lib/bus';

interface Layer { hls: Hls | null; video: HTMLVideoElement; }

let spotEl: HTMLElement;
let stageEl: HTMLElement;
let loadingEl: HTMLElement;
let filmEl: HTMLElement;
let qualityBtn: HTMLButtonElement;
let muteBtn: HTMLButtonElement;

let openId: string | null = null;
let current: Layer | null = null;
let token = 0;
let refreshTimer: number | undefined; // yoonee token rotation while focused
let muted = true;
const zoom = { s: 1, tx: 0, ty: 0 };
const MIN = 1;
const MAX = 6;

export const isSpotlightOpen = (): boolean => openId !== null;

export function initSpotlight(): void {
  spotEl = must('#spotlight');
  spotEl.innerHTML = `
    <div class="spot-stage" id="spotStage">
      <div class="spot-loading" id="spotLoading"><div class="spin"></div></div>
    </div>
    <div class="spot-overlay">
      <div class="live-badge"><span class="live-dot"></span><span class="lt">LIVE</span> <span class="spot-name mono" id="spotName"></span></div>
      <div class="spot-actions">
        <button class="ico" id="spotZoomReset" title="รีเซ็ตซูม (1:1)">${ICON.zoomReset}</button>
        <button class="ico" id="spotQuality" title="คุณภาพ"></button>
        <button class="ico" id="spotMute" title="เสียง">${ICON.mute}</button>
        <button class="ico" id="spotClose" title="ปิด (Esc)">${ICON.x}</button>
      </div>
    </div>
    <div class="film" id="film"></div>`;
  stageEl = must('#spotStage', spotEl);
  loadingEl = must('#spotLoading', spotEl);
  filmEl = must('#film', spotEl);
  qualityBtn = must<HTMLButtonElement>('#spotQuality', spotEl);
  muteBtn = must<HTMLButtonElement>('#spotMute', spotEl);

  must('#spotClose', spotEl).addEventListener('click', close);
  must('#spotZoomReset', spotEl).addEventListener('click', () => resetZoom());
  qualityBtn.addEventListener('click', cycleQuality);
  muteBtn.addEventListener('click', toggleMute);
  bindZoom();

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openId) close(); });
  bus.on('cam:deleted', (id) => { if (openId === id) close(); });
}

export function openSpotlight(id: string): void {
  if (!state.cameras[id]) return;
  if (openId === id) return;
  const first = openId === null;
  if (first) {
    pool.paused = true;
    for (const t of [...pool.attached]) t.detach(true);
    spotEl.classList.add('open');
    document.body.classList.add('spot-open');
  }
  void switchTo(id);
}

function close(): void {
  if (openId === null) return;
  token++;
  clearInterval(refreshTimer);
  if (current) { current.hls?.destroy(); current.video.remove(); current = null; }
  openId = null;
  resetZoom();
  spotEl.classList.remove('open');
  document.body.classList.remove('spot-open');
  filmEl.innerHTML = '';
  pool.paused = false;
  pool.reconcile();
}

async function switchTo(id: string): Promise<void> {
  const my = ++token;
  openId = id;
  updateChrome();
  const v = makeVideo();
  v.className = 'spot-layer spot-next';
  v.preload = 'auto';
  v.muted = muted;
  stageEl.appendChild(v);

  const q = effectiveQuality(id);
  const cam = state.cameras[id]!;
  const yoonee = isYoonee(cam);
  let url: string;
  if (yoonee) {
    if (!canPlayNativeHls(v)) { v.remove(); return; } // media origin sends no CORS
    try { url = await cctvStreamUrl(id); }
    catch { v.remove(); return; }
    if (my !== token) { v.remove(); return; }
  } else {
    url = masterUrl(id);
  }
  let hls: Hls | null = null;

  if (canPlayNativeHls(v)) {
    v.src = url;
  } else {
    let Ctor: typeof Hls;
    try { Ctor = await ensureHls(); } catch { v.remove(); return; }
    if (my !== token) { v.remove(); return; }
    if (!Ctor.isSupported()) { v.remove(); return; }
    hls = new Ctor(focusConfig());
    hls.loadSource(url);
    hls.attachMedia(v);
    hls.autoLevelCapping = q === 'AUTO' ? -1 : LEVEL_IDX[q];
  }
  playSafe(v);

  const spinner = window.setTimeout(() => { if (token === my) loadingEl.classList.add('show'); }, 250);
  await ready(hls, v, 4000);
  clearTimeout(spinner);
  if (my !== token) { hls?.destroy(); v.remove(); return; }
  loadingEl.classList.remove('show');

  v.style.opacity = '1';
  if (current) current.video.style.opacity = '0';
  await sleep(280);
  if (my !== token) { hls?.destroy(); v.remove(); return; }

  if (current) { current.hls?.destroy(); current.video.remove(); }
  v.classList.replace('spot-next', 'spot-current');
  current = { hls, video: v };
  resetZoom();

  clearInterval(refreshTimer);
  if (yoonee) {
    refreshTimer = window.setInterval(() => {
      if (token !== my || !current) return;
      if (Date.now() <= cctvTokenExpiry() - 15_000) return;
      void (async () => {
        try {
          const next = await cctvStreamUrl(id);
          if (token !== my || !current) return;
          if (current.hls) current.hls.loadSource(next);
          else current.video.src = next;
        } catch { /* ignore; hls error handling will retry */ }
      })();
    }, 5000);
  }
}

function ready(hls: Hls | null, v: HTMLVideoElement, ms: number): Promise<void> {
  return new Promise((res) => {
    let done = false;
    let frag = !hls;
    let playing = false;
    const Events = hls ? (hls.constructor as typeof Hls).Events : null;
    const onFrag = (): void => { frag = true; check(); };
    const onPlay = (): void => { playing = true; check(); };
    const fin = (): void => {
      if (done) return;
      done = true;
      if (hls && Events) hls.off(Events.FRAG_BUFFERED, onFrag);
      v.removeEventListener('playing', onPlay);
      clearTimeout(t);
      res();
    };
    const check = (): void => { if (frag && playing) fin(); };
    if (hls && Events) hls.on(Events.FRAG_BUFFERED, onFrag);
    v.addEventListener('playing', onPlay);
    v.requestVideoFrameCallback?.(() => { playing = true; check(); });
    const t = window.setTimeout(fin, ms);
  });
}

function updateChrome(): void {
  if (!openId) return;
  const cam = state.cameras[openId];
  if (cam) (must('#spotName', spotEl)).textContent = labelOf(cam);
  const yoonee = !!cam && isYoonee(cam);
  qualityBtn.style.display = yoonee ? 'none' : '';
  if (!yoonee) {
    qualityBtn.textContent = effectiveQuality(openId);
    qualityBtn.classList.toggle('is-override', !!state.quality[openId]);
  }
  renderFilm();
}

function renderFilm(): void {
  filmEl.innerHTML = '';
  for (const id of viewCams()) {
    const cam = state.cameras[id];
    if (!cam) continue;
    const b = document.createElement('button');
    b.className = 'film-b' + (openId === id ? ' cur' : '');
    b.innerHTML = `<span class="fn">${esc(labelOf(cam))}</span><span class="fi">${cam.id}</span>`;
    b.addEventListener('click', () => switchTo(id));
    filmEl.appendChild(b);
  }
}

function cycleQuality(): void {
  if (!openId || isYoonee(state.cameras[openId]!)) return;
  const cur = effectiveQuality(openId);
  const next = QUALITY_CYCLE[(QUALITY_CYCLE.indexOf(cur as never) + 1) % QUALITY_CYCLE.length]! as Quality;
  if (next === state.globalQuality) delete state.quality[openId];
  else state.quality[openId] = next;
  save();
  updateChrome();
  if (current?.hls) {
    current.hls.autoLevelCapping = next === 'AUTO' ? -1 : LEVEL_IDX[next];
    if (next !== 'AUTO') current.hls.nextLevel = LEVEL_IDX[next];
  }
  refreshQualityLabels();
}

function toggleMute(): void {
  muted = !muted;
  if (current) current.video.muted = muted;
  muteBtn.innerHTML = muted ? ICON.mute : ICON.vol;
  muteBtn.setAttribute('aria-pressed', String(!muted));
}

// ---- zoom + pan (digital) ----
function applyZoom(): void {
  if (current) current.video.style.transform = `translate(${zoom.tx}px,${zoom.ty}px) scale(${zoom.s})`;
  spotEl.classList.toggle('zoomed', zoom.s > 1.01);
}
function resetZoom(): void { zoom.s = 1; zoom.tx = 0; zoom.ty = 0; applyZoom(); }
function clampPan(box: DOMRect): void {
  const minX = box.width - zoom.s * box.width;
  const minY = box.height - zoom.s * box.height;
  zoom.tx = Math.min(0, Math.max(minX, zoom.tx));
  zoom.ty = Math.min(0, Math.max(minY, zoom.ty));
}
function zoomAt(px: number, py: number, factor: number, box: DOMRect): void {
  const s2 = clamp(zoom.s * factor, MIN, MAX);
  const k = s2 / zoom.s;
  zoom.tx = px - k * (px - zoom.tx);
  zoom.ty = py - k * (py - zoom.ty);
  zoom.s = s2;
  if (zoom.s < 1.02) { zoom.s = 1; zoom.tx = 0; zoom.ty = 0; }
  clampPan(box);
  applyZoom();
}

function bindZoom(): void {
  const pts = new Map<number, PointerEvent>();
  let pinchStart = 0;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let lastTap = 0;
  const box = (): DOMRect => stageEl.getBoundingClientRect();

  stageEl.addEventListener('pointerdown', (e) => {
    pts.set(e.pointerId, e);
    if (pts.size === 2) {
      const [a, b] = [...pts.values()];
      pinchStart = dist(a!.clientX, a!.clientY, b!.clientX, b!.clientY);
      dragging = false;
    } else {
      const now = performance.now();
      if (now - lastTap < 300) { resetZoom(); dragging = false; lastTap = 0; return; }
      lastTap = now;
      if (zoom.s > 1) { dragging = true; lastX = e.clientX; lastY = e.clientY; try { stageEl.setPointerCapture(e.pointerId); } catch { /* noop */ } }
    }
  });
  stageEl.addEventListener('pointermove', (e) => {
    if (!pts.has(e.pointerId)) return;
    pts.set(e.pointerId, e);
    const b = box();
    if (pts.size === 2) {
      const [p, q] = [...pts.values()];
      const d = dist(p!.clientX, p!.clientY, q!.clientX, q!.clientY);
      const midX = (p!.clientX + q!.clientX) / 2 - b.left;
      const midY = (p!.clientY + q!.clientY) / 2 - b.top;
      const factor = pinchStart ? d / pinchStart : 1;
      pinchStart = d;
      zoomAt(midX, midY, factor, b);
    } else if (dragging) {
      zoom.tx += e.clientX - lastX;
      zoom.ty += e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;
      clampPan(b);
      applyZoom();
    }
  });
  const up = (e: PointerEvent): void => {
    pts.delete(e.pointerId);
    if (pts.size < 2) pinchStart = 0;
    if (pts.size === 0) dragging = false;
  };
  stageEl.addEventListener('pointerup', up);
  stageEl.addEventListener('pointercancel', up);
  stageEl.addEventListener('wheel', (e) => {
    e.preventDefault();
    const b = box();
    zoomAt(e.clientX - b.left, e.clientY - b.top, e.deltaY < 0 ? 1.15 : 1 / 1.15, b);
  }, { passive: false });
  stageEl.addEventListener('dblclick', () => resetZoom());
}
